/**
 * PATH: packages/bills/src/components/useFieldHighlight.ts
 * FILE_ID: BILLS-PKG-CMP-FIELDHL-001
 * PURPOSE: When a field is selected in the Read pane, scroll the edit
 *          form to the matching input and ring-highlight it (and clear
 *          the ring when selection moves).
 *
 * WHY: p1c cross-highlight — clicking a cell in the structured read must
 *      visibly point at the same field in the form. Done by data-attr
 *      lookup so the form inputs only need a `data-bill-field` tag, not a
 *      prop threaded through every <Field>. Pure DOM, isolated + testable.
 */

import { useEffect, type RefObject } from 'react';

const RING = ['ring-2', 'ring-primary/50', 'ring-offset-1', 'rounded-md'];

/**
 * Highlight `[data-bill-field="<activeField>"]` inside `containerRef`.
 * @param containerRef scroll container holding the tagged inputs
 * @param activeField  currently-selected field key (or null)
 * @param scroll       whether to scrollIntoView the match (default true)
 */
export function useFieldHighlight(
  containerRef: RefObject<HTMLElement | null>,
  activeField: string | null | undefined,
  scroll = true,
): void {
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    if (!activeField) return;
    const el = root.querySelector<HTMLElement>(
      `[data-bill-field="${cssEscape(activeField)}"]`,
    );
    if (!el) return;
    el.classList.add(...RING);
    if (scroll) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    return () => {
      el.classList.remove(...RING);
    };
  }, [containerRef, activeField, scroll]);
}

/** Minimal CSS.escape fallback for the attribute-selector value. */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, '\\$&');
}
